import { ApiResponse } from '@nf/types/api';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useApiBackendInfo } from '@/hooks/query/use-api-backend-info';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { ApiBackendInfo } from '@/hooks/query/athena/types';
import { WcPromotionLotterysDto } from './types';

/** `pageSize === 0` 時 API 回傳該獎池全部彩券。 */
export const fetchWcPromotionLotterys = async (
	apiBackendInfo: ApiBackendInfo,
	poolId: string,
	page: number,
	pageSize: number
): Promise<ApiResponse<WcPromotionLotterysDto>> => {
	const response = await fetchComposer.getWithBearer(
		`${apiBackendInfo.url}/WcPromotion/GetWcPromotionLotterys?poolId=${encodeURIComponent(poolId)}&page=${page}&pageSize=${pageSize}`
	)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

export const useWcPromotionLotterys = (poolId: string, page: number, pageSize: number) => {
	const { enableWcPromotionLuckySpin, enableWcPromotionWeeklyLottery, enableWcPromotionAfterParty } = useLicSettings();
	const apiBackendInfo = useApiBackendInfo();
	const isWcPromotionEnabled = enableWcPromotionLuckySpin || enableWcPromotionWeeklyLottery || enableWcPromotionAfterParty;

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [
			queryKeys.WC_PROMOTION_LOTTERYS,
			apiBackendInfo.url,
			apiBackendInfo.token,
			poolId,
			page,
			pageSize
		],
		queryFn: () => fetchWcPromotionLotterys(apiBackendInfo, poolId, page, pageSize),
		staleTime: 1000 * 10,
		cacheTime: 1000 * 10,
		enabled:
			isWcPromotionEnabled &&
			apiBackendInfo.isReady &&
			!!poolId &&
			page >= 1 &&
			(pageSize > 0 || pageSize === 0)
	});

	return {
		data: data?.Data,
		isLoading,
		isError,
		error,
		refetch
	};
};
